import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import './ArchiveAudioPage.css';
import { trackPageView } from '../lib/supabase';
import { findItem, backLinkFor } from '../lib/menuTree';
import TopBar from './TopBar';

const IA_BASE = 'https://archive.org';

async function fetchTracks(identifier) {
  const res = await fetch(`${IA_BASE}/metadata/${identifier}`);
  if (!res.ok) throw new Error(`IA API error ${res.status}`);
  const json = await res.json();

  const audio = (json.files || []).filter(f => {
    const name = f.name?.toLowerCase() ?? '';
    const fmt  = (f.format ?? '').toLowerCase();
    return (name.endsWith('.mp3') || name.endsWith('.ogg') || name.endsWith('.flac') ||
            fmt.includes('vbr mp3') || fmt.includes('mp3') || fmt.includes('ogg'));
  });

  // De-prefer derivative files (they have source field set)
  audio.sort((a, b) => {
    const aOrig = !a.source || a.source === 'original' ? 0 : 1;
    const bOrig = !b.source || b.source === 'original' ? 0 : 1;
    return aOrig - bOrig || (a.name ?? '').localeCompare(b.name ?? '');
  });

  return audio.map(f => ({
    name: f.title || f.name?.replace(/\.[^.]+$/, '').replace(/_/g, ' '),
    url:  `${IA_BASE}/download/${identifier}/${encodeURIComponent(f.name)}`,
    length: f.length,
  }));
}

function formatDuration(secs) {
  if (!secs) return '';
  const s = Math.round(parseFloat(secs));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export default function ArchiveAudioPage({ data }) {
  const params = useParams();
  const { categoryId, subcategoryId } = params;

  const item       = findItem(data, params);
  const title      = item?.name ?? 'Archive Audio';
  const identifier = item?.link ?? '';

  const [tracks,     setTracks]     = useState([]);
  const [fetchError, setFetchError] = useState(null);
  const [trackIdx,   setTrackIdx]   = useState(0);

  const audioRef    = useRef(null);
  const canvasRef   = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const animRef     = useRef(null);
  const sourceRef   = useRef(null);

  const [isPlaying,   setIsPlaying]   = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration,    setDuration]    = useState(0);
  const [volume,      setVolume]      = useState(0.85);
  const [isMuted,     setIsMuted]     = useState(false);

  useEffect(() => {
    if (!identifier) return;
    setTracks([]);
    setFetchError(null);
    setTrackIdx(0);
    trackPageView(title, parseInt(categoryId, 10), subcategoryId ? parseInt(subcategoryId, 10) : null);
    fetchTracks(identifier)
      .then(setTracks)
      .catch(err => setFetchError(err.message));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identifier]);

  // Draw visualizer loop
  const drawLoop = useCallback(() => {
    const canvas   = canvasRef.current;
    const analyser = analyserRef.current;
    if (!canvas || !analyser) return;

    const ctx    = canvas.getContext('2d');
    const W      = canvas.width;
    const H      = canvas.height;
    const bufLen = analyser.frequencyBinCount;
    const data   = new Uint8Array(bufLen);

    const draw = () => {
      animRef.current = requestAnimationFrame(draw);
      analyser.getByteFrequencyData(data);

      ctx.clearRect(0, 0, W, H);

      // Dark gradient background
      const bg = ctx.createLinearGradient(0, 0, 0, H);
      bg.addColorStop(0, '#0d0d1a');
      bg.addColorStop(1, '#1a0f0a');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, W, H);

      const barW  = (W / bufLen) * 2.2;
      const gap   = 1.5;
      let   x     = 0;

      for (let i = 0; i < bufLen; i++) {
        const pct    = data[i] / 255;
        const barH   = pct * H * 0.85;

        // Warm amber → gold gradient per bar
        const grad = ctx.createLinearGradient(0, H - barH, 0, H);
        grad.addColorStop(0, `hsla(${40 + pct * 20}, 100%, 72%, 0.9)`);
        grad.addColorStop(1, `hsla(${20 + pct * 20}, 90%, 45%, 0.7)`);
        ctx.fillStyle = grad;

        // Rounded top
        const r = Math.min(barW / 2, 3);
        ctx.beginPath();
        ctx.moveTo(x + r, H - barH);
        ctx.lineTo(x + barW - r, H - barH);
        ctx.quadraticCurveTo(x + barW, H - barH, x + barW, H - barH + r);
        ctx.lineTo(x + barW, H);
        ctx.lineTo(x, H);
        ctx.lineTo(x, H - barH + r);
        ctx.quadraticCurveTo(x, H - barH, x + r, H - barH);
        ctx.fill();

        // Reflection (subtle)
        ctx.fillStyle = `hsla(40, 100%, 60%, ${pct * 0.12})`;
        ctx.fillRect(x, H - barH * 0.25, barW, barH * 0.25);

        x += barW + gap;
      }
    };

    draw();
  }, []);

  const setupAudioContext = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || audioCtxRef.current) return;

    const ctx      = new (window.AudioContext || window.webkitAudioContext)();
    const analyser = ctx.createAnalyser();
    analyser.fftSize             = 128;
    analyser.smoothingTimeConstant = 0.82;

    const source = ctx.createMediaElementSource(audio);
    source.connect(analyser);
    analyser.connect(ctx.destination);

    audioCtxRef.current = ctx;
    analyserRef.current = analyser;
    sourceRef.current   = source;

    drawLoop();
  }, [drawLoop]);

  useEffect(() => () => {
    cancelAnimationFrame(animRef.current);
    audioCtxRef.current?.close();
  }, []);

  const handlePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    setupAudioContext();
    if (audioCtxRef.current?.state === 'suspended') {
      await audioCtxRef.current.resume();
    }
    audio.play();
  };

  const handlePause = () => audioRef.current?.pause();

  const handlePlayPause = () => (isPlaying ? handlePause() : handlePlay());

  const handleSelectTrack = (idx) => {
    setTrackIdx(idx);
    setCurrentTime(0);
    setIsPlaying(false);
    // Audio element src will update → auto-play via onLoadedMetadata if desired
  };

  const handlePrev = () => handleSelectTrack(Math.max(0, trackIdx - 1));
  const handleNext = () => handleSelectTrack(Math.min(tracks.length - 1, trackIdx + 1));

  const handleSeek = (e) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const t    = ((e.clientX - rect.left) / rect.width) * duration;
    audioRef.current.currentTime = t;
    setCurrentTime(t);
  };

  const handleVolumeChange = (e) => {
    const v = parseFloat(e.target.value);
    setVolume(v);
    setIsMuted(v === 0);
    if (audioRef.current) audioRef.current.volume = v;
  };

  const handleMuteToggle = () => {
    if (!audioRef.current) return;
    if (isMuted) { audioRef.current.volume = volume; setIsMuted(false); }
    else         { audioRef.current.volume = 0;      setIsMuted(true);  }
  };

  const fmt = (t) => `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, '0')}`;

  const currentTrack = tracks[trackIdx];

  return (
    <div className="archive-audio-page">
      <TopBar backTo={backLinkFor(params)} title={title} />
      <div className="top-bar-spacer" />

      {fetchError && (
        <div className="archive-error">Could not load tracks: {fetchError}</div>
      )}

      {!fetchError && tracks.length === 0 && (
        <div className="archive-loading">Loading tracks from Internet Archive…</div>
      )}

      {tracks.length > 0 && (
        <div className="audio-layout">

          {/* ── Track list ────────────────────────────────────────── */}
          <aside className="track-list">
            <div className="track-list-header">
              {tracks.length} Episode{tracks.length !== 1 ? 's' : ''}
            </div>
            <ul>
              {tracks.map((t, i) => (
                <li
                  key={i}
                  className={`track-item ${i === trackIdx ? 'active' : ''}`}
                  onClick={() => handleSelectTrack(i)}
                >
                  <span className="track-num">{i + 1}</span>
                  <span className="track-name">{t.name}</span>
                  {t.length && (
                    <span className="track-dur">{formatDuration(t.length)}</span>
                  )}
                </li>
              ))}
            </ul>
          </aside>

          {/* ── Player + Visualizer ────────────────────────────────── */}
          <main className="player-area">
            <canvas
              ref={canvasRef}
              className="visualizer-canvas"
              width={600}
              height={220}
            />

            {/* Now playing label */}
            <div className="now-playing">
              <div className="now-playing-label">NOW PLAYING</div>
              <div className="now-playing-title">{currentTrack?.name}</div>
            </div>

            {/* Progress bar */}
            <div className="audio-progress-wrap" onClick={handleSeek}>
              <div
                className="audio-progress-fill"
                style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
              />
            </div>
            <div className="audio-time-row">
              <span>{fmt(currentTime)}</span>
              <span>{fmt(duration)}</span>
            </div>

            {/* Controls */}
            <div className="audio-controls">
              <button className="audio-btn" onClick={handlePrev} disabled={trackIdx === 0} aria-label="Previous">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6 6h2v12H6zm3.5 6 8.5 6V6z"/>
                </svg>
              </button>

              <button className="audio-btn play-btn" onClick={handlePlayPause} aria-label={isPlaying ? 'Pause' : 'Play'}>
                {isPlaying
                  ? <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
                  : <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>}
              </button>

              <button className="audio-btn" onClick={handleNext} disabled={trackIdx >= tracks.length - 1} aria-label="Next">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z"/>
                </svg>
              </button>
            </div>

            {/* Volume */}
            <div className="volume-row">
              <button className="audio-btn small" onClick={handleMuteToggle} aria-label="Mute toggle">
                {isMuted || volume === 0
                  ? <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/></svg>
                  : <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/></svg>}
              </button>
              <input
                type="range" min="0" max="1" step="0.05"
                value={isMuted ? 0 : volume}
                onChange={handleVolumeChange}
                className="audio-volume-slider"
                aria-label="Volume"
              />
            </div>
          </main>
        </div>
      )}

      {/* Hidden audio element */}
      {currentTrack && (
        <audio
          ref={audioRef}
          src={currentTrack.url}
          crossOrigin="anonymous"
          onTimeUpdate={() => audioRef.current && setCurrentTime(audioRef.current.currentTime)}
          onLoadedMetadata={() => audioRef.current && setDuration(audioRef.current.duration)}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={handleNext}
        />
      )}
    </div>
  );
}
